import {getWorkspaceLayoutSettingWithDefaults} from '../../components/WorkspaceDrawer/Settings/workspaceLayout/getWorkspaceLayoutSettings';
import {RUN_VIEW_TYPE} from '../../pages/Run/types';
import {getDatadogRum} from '../../util/datadog/datadogRum';
import {isAutoPanel} from '../../util/isAutoPanel';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../util/panelbankTypes';
import * as Panels from '../../util/panels';
import {compressFlowConfig} from '../../util/sectionFlowConfig';
import {
  isSparseCompressionEnabled,
  isSparseOverridesEnabled,
} from '../../util/sparseOverridesFeatureFlag';
import {isReportLikeViewType} from './report/types';
import type {
  MultiRunWorkspaceViewType,
  RunWorkspaceViewType,
  ViewType,
  WholeFromType,
} from './types';

/**
 * Removes default/auto data from view spec so that the smallest,
 * most compact version of the spec can be persisted. Fields should
 * only be compressed if the app is set up to rehydrate that data
 * when the spec is loaded again.
 *
 * A smaller spec means faster read/write over the network, but may
 * result in additional processing time when rehydrating the data.
 * Be mindful of this tradeoff when compressing any fields.
 */
export function compressView<T extends ViewType>(
  spec: WholeFromType<T>,
  type: T
) {
  // report views don't get compressed
  if (isReportLikeViewType(type)) {
    return spec;
  }

  if (type === RUN_VIEW_TYPE) {
    // Typescript is being uncooperative. If `type` of type `T` equals 'run-view',
    // then `spec` of type `WholeFromType<T>` should be `WholeFromType<'run-view'>`.
    // TS is not grokking that for some reason, so we're forced to type cast here.
    return compressRunWorkspace(spec as WholeFromType<RunWorkspaceViewType>);
  }

  return compressMultiRunWorkspace(
    spec as WholeFromType<MultiRunWorkspaceViewType>
  );
}

function compressRunWorkspace(spec: WholeFromType<RunWorkspaceViewType>) {
  const {panelBankConfig, workspaceSettings} = spec;
  return {
    ...spec,
    panelBankConfig: compressPanelBankConfig(
      panelBankConfig,
      workspaceSettings
    ),
  };
}

function compressMultiRunWorkspace(
  spec: WholeFromType<MultiRunWorkspaceViewType>
) {
  const {panelBankConfig, workspaceSettings} = spec.section;
  return {
    ...spec,
    section: {
      ...spec.section,
      panelBankConfig: compressPanelBankConfig(
        panelBankConfig,
        workspaceSettings
      ),
    },
  };
}

function compressPanelBankConfig(
  panelBankConfig: WholeFromType<'panel-bank-config'>,
  workspaceSettings?: WholeFromType<'workspace-settings'>
) {
  const sparseEnabled = isSparseOverridesEnabled();
  const compressionEnabled = isSparseCompressionEnabled();

  // Track which sections have overrides (for Phase 1 safety and Phase 2 eligibility)
  const sectionsWithPlacementOverrides = new Set<string>();
  const sectionsWithConfigOverrides = new Set<string>();

  if (sparseEnabled && panelBankConfig.panelPlacementOverrides) {
    for (const override of Object.values(
      panelBankConfig.panelPlacementOverrides
    )) {
      sectionsWithPlacementOverrides.add(override.sectionId);
    }
  }

  if (sparseEnabled && panelBankConfig.panelConfigOverrides) {
    for (const section of panelBankConfig.sections) {
      for (const panel of section.panels) {
        const key = Panels.getOverrideKey(panel);
        if (key != null && key in panelBankConfig.panelConfigOverrides) {
          sectionsWithConfigOverrides.add(section.__id__);
          break;
        }
      }
    }
  }

  const {compressedSections, telemetry} = panelBankConfig.sections.reduce(
    (acc, section) => {
      const hasOverrides =
        sectionsWithPlacementOverrides.has(section.__id__) ||
        sectionsWithConfigOverrides.has(section.__id__);

      // Track which compression path each section takes
      if (compressionEnabled) {
        acc.telemetry.sectionPaths.phase2SectionCount++;
      } else if (sparseEnabled && hasOverrides) {
        acc.telemetry.sectionPaths.phase1SectionCount++;
      } else {
        acc.telemetry.sectionPaths.legacySectionCount++;
      }

      // Collect per-panel telemetry data in the same pass
      acc.telemetry.panelData.validSectionIds.add(section.__id__);
      for (const panel of section.panels) {
        acc.telemetry.panelData.totalPanelCount++;
        if (panel.isAuto === false) {
          acc.telemetry.panelData.customPanelCount++;
        }
        const key = Panels.getOverrideKey(panel);
        if (key != null) {
          acc.telemetry.panelData.validPanelKeys.add(key);
        }
      }

      const result = compressPanelBankSectionConfig(
        section,
        workspaceSettings,
        sparseEnabled,
        compressionEnabled,
        hasOverrides
      );

      // Track non-compressible panels: sections where phase 2 couldn't strip
      // auto panels. Compare input vs output panel count to avoid recomputing
      // isSectionPanelsAuto (which iterates all panels).
      if (
        compressionEnabled &&
        result.panels.length === section.panels.length
      ) {
        acc.telemetry.sectionPaths.nonCompressiblePanelCount +=
          section.panels.length;
      }

      acc.compressedSections.push(result);
      return acc;
    },
    {
      compressedSections: [] as WholeFromType<'panel-bank-section-config'>[],
      telemetry: {
        sectionPaths: {
          phase2SectionCount: 0,
          phase1SectionCount: 0,
          legacySectionCount: 0,
          nonCompressiblePanelCount: 0,
        },
        panelData: {
          totalPanelCount: 0,
          customPanelCount: 0,
          validPanelKeys: new Set<string>(),
          validSectionIds: new Set<string>(),
        },
      },
    }
  );

  // Emit save telemetry
  emitSaveTelemetry(
    panelBankConfig,
    compressedSections,
    sparseEnabled,
    compressionEnabled,
    telemetry.sectionPaths,
    telemetry.panelData
  );

  return {
    ...panelBankConfig,
    sections: compressedSections,
    // Always preserve overrides if they exist — even when flags are off,
    // the data must survive so it can be re-applied when flags come back
    // (e.g., after a transient Statsig outage).
    panelPlacementOverrides: panelBankConfig.panelPlacementOverrides,
    panelConfigOverrides: panelBankConfig.panelConfigOverrides,
  };
}

function compressPanelBankSectionConfig(
  panelBankSectionConfig: WholeFromType<'panel-bank-section-config'>,
  workspaceSettings: WholeFromType<'workspace-settings'> | undefined,
  sparseEnabled: boolean,
  compressionEnabled: boolean,
  hasOverrides: boolean
) {
  const {name, flowConfig, panels} = panelBankSectionConfig;

  // When COMPRESSION is enabled AND section is auto-compressible, we use selective persistence:
  // - Drop auto-generated panels (they will be regenerated on load)
  // - Keep custom panels (isAuto === false) which must be persisted
  // This allows custom panels (bar charts, scatter plots, etc.) to coexist
  // with auto panels without forcing the entire section to be persisted.
  let compressedPanels: typeof panels;
  let persistedIsPanelsAuto = panelBankSectionConfig.isPanelsAuto;

  if (compressionEnabled) {
    // Phase 2: Full sparse mode - strip auto panels when eligible
    // If section has overrides, treat as eligible even if isPanelsAuto was set to false
    // during Phase 1 (this handles the Phase 1 → Phase 2 migration)
    const canDropAutoPanels =
      hasOverrides ||
      isSectionPanelsAuto(panelBankSectionConfig, workspaceSettings);
    if (canDropAutoPanels) {
      // Keep custom panels (isAuto === false), drop auto-generated panels (isAuto === true)
      compressedPanels = panels.filter(panel => panel.isAuto === false);
      // Ensure isPanelsAuto is true for sections with overrides (they're managed by sparse system)
      if (hasOverrides) {
        persistedIsPanelsAuto = true;
      }
    } else {
      compressedPanels = panels;
    }
  } else if (sparseEnabled && hasOverrides) {
    // Phase 1: Overrides ON but Compression OFF
    // Set isPanelsAuto: false so that if we roll back the client code,
    // the old client will preserve the panels (safe rollback)
    compressedPanels = panels;
    persistedIsPanelsAuto = false;
  } else {
    // Legacy behavior: strip auto-generated panels from auto sections.
    // Always preserve custom panels (isAuto === false) — these are user-created
    // and cannot be regenerated. This protects against data loss if sparse
    // compression was previously ON (storing only custom panels) and flags
    // revert to OFF (e.g., Statsig outage).
    if (isSectionPanelsAuto(panelBankSectionConfig, workspaceSettings)) {
      compressedPanels = panels.filter(panel => panel.isAuto === false);
    } else {
      compressedPanels = panels;
    }
  }

  return {
    ...panelBankSectionConfig,
    isPanelsAuto: persistedIsPanelsAuto,
    flowConfig: compressFlowConfig(name, flowConfig),
    panels: compressedPanels,
  };
}

// Rough estimate of bytes per auto panel config (typical Run History Line Plot).
// Used for estimated space savings without the cost of JSON.stringify.
const ESTIMATED_BYTES_PER_AUTO_PANEL = 250;

function emitSaveTelemetry(
  panelBankConfig: WholeFromType<'panel-bank-config'>,
  compressedSections: WholeFromType<'panel-bank-section-config'>[],
  sparseEnabled: boolean,
  compressionEnabled: boolean,
  sectionPathCounts: {
    phase2SectionCount: number;
    phase1SectionCount: number;
    legacySectionCount: number;
    nonCompressiblePanelCount: number;
  },
  panelData: {
    totalPanelCount: number;
    customPanelCount: number;
    validPanelKeys: Set<string>;
    validSectionIds: Set<string>;
  }
) {
  const placementOverrides = panelBankConfig.panelPlacementOverrides ?? {};
  const configOverrides = panelBankConfig.panelConfigOverrides ?? {};
  const placementOverrideCount = Object.keys(placementOverrides).length;
  const configOverrideCount = Object.keys(configOverrides).length;

  // Nothing to report if there are no overrides and flags are off
  if (
    placementOverrideCount === 0 &&
    configOverrideCount === 0 &&
    !sparseEnabled
  ) {
    return;
  }

  const {totalPanelCount, customPanelCount, validPanelKeys, validSectionIds} =
    panelData;

  // Count orphaned overrides
  let orphanedPlacementCount = 0;
  for (const [key, override] of Object.entries(placementOverrides)) {
    if (!validPanelKeys.has(key) || !validSectionIds.has(override.sectionId)) {
      orphanedPlacementCount++;
    }
  }

  let orphanedConfigCount = 0;
  for (const key of Object.keys(configOverrides)) {
    if (!validPanelKeys.has(key)) {
      orphanedConfigCount++;
    }
  }

  // Estimate space savings: count auto panels stripped by compression
  let autoPanelsStripped = 0;
  for (let i = 0; i < panelBankConfig.sections.length; i++) {
    autoPanelsStripped +=
      panelBankConfig.sections[i].panels.length -
      compressedSections[i].panels.length;
  }
  const estimatedBytesSaved =
    autoPanelsStripped * ESTIMATED_BYTES_PER_AUTO_PANEL;

  // Detect flag mismatch: overrides exist but flags are off
  const hasOverridesWithFlagsOff =
    !sparseEnabled && (placementOverrideCount > 0 || configOverrideCount > 0);

  getDatadogRum().addAction('sparse_overrides.save', {
    placementOverrideCount,
    configOverrideCount,
    orphanedPlacementCount,
    orphanedConfigCount,
    customPanelCount,
    totalPanelCount,
    autoPanelsStripped,
    estimatedBytesSaved,
    overridesEnabled: sparseEnabled,
    compressionEnabled,
    hasOverridesWithFlagsOff,
    ...sectionPathCounts,
  });
}

/**
 * Determine whether it's safe to drop all panels in a section
 * and let them be regenerated at load time
 */
export function isSectionPanelsAuto(
  panelBankSectionConfig: WholeFromType<'panel-bank-section-config'>,
  workspaceSettings?: WholeFromType<'workspace-settings'>
): boolean {
  const {name, isPanelsAuto, panels} = panelBankSectionConfig;

  // If section is explicitly marked with isPanelsAuto flag, use that
  if (typeof isPanelsAuto === 'boolean') {
    return isPanelsAuto;
  }

  // Hidden section should never be compressed
  if (name === PANEL_BANK_HIDDEN_SECTION_NAME) {
    return false;
  }

  // If all panels in section are auto, it *might* be safe to compress.
  // It's still possible that some panels were deleted, in which case
  // we must ensure those don't get added back. We can only do this if
  // panel auto gen is enabled, since deleted panels will be tracked in
  // the Hidden Panels section and won't be added again.

  const {shouldAutoGeneratePanels, autoOrganizePrefix} =
    getWorkspaceLayoutSettingWithDefaults(workspaceSettings ?? {});

  if (!shouldAutoGeneratePanels) {
    return false;
  }

  return panels.every(panel => {
    return isAutoPanel(
      panel,
      name,
      Panels.getKey(panel),
      undefined,
      autoOrganizePrefix
    );
  });
}
